// src/features/availability/lib/coverage.ts
import type { AvailabilityRow, BlockAssignmentRow } from '../types';

import { timeToMinutes } from './time';

/**
 * Forward window the practitioner must keep "decided" to stay on cadence.
 * The cadenceMiddleware on the backend hard-blocks clinical write endpoints
 * once any day in [today, today + N - 1] has zero non-deleted rows.
 *
 * Mirrors `PRACTITIONER_DECIDED_FORWARD_DAYS` in elixaa-core/src/constants.ts.
 */
export const PRACTITIONER_DECIDED_FORWARD_DAYS = 14;

/**
 * A date is "decided" iff it has at least one non-deleted row — a working
 * block OR an off marker, partial OR full day. The signal is "did the
 * practitioner touch this day?", not "is every minute accounted for". A
 * 9–12 working block on its own counts.
 *
 * Mirrors `getPractitionerCadenceStatus` in elixaa-core.
 */
export function isDayDecided(rows: readonly AvailabilityRow[]): boolean {
  return rows.length > 0;
}

/**
 * A date is "fully covered" iff its non-deleted rows tile every minute of
 * [00:00, 24:00) without gaps. Distinct from `isDayDecided` — full coverage
 * is no longer a cadence requirement, but the calendar's "fill remaining
 * gaps as OFF" affordance still keys off it.
 */
export function isDayFullyCovered(rows: readonly AvailabilityRow[]): boolean {
  if (rows.length === 0) return false;
  const intervals = rows
    .map((r) => [timeToMinutes(r.startTime), timeToMinutes(r.endTime)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  let cursor = 0;
  for (const [start, end] of intervals) {
    if (start > cursor) return false;
    if (end > cursor) cursor = end;
  }
  return cursor >= 1440;
}

/**
 * True when at least one working block (mode !== null) on the day has time
 * not yet covered by an assignment. Admins act on this — it's the
 * "click-to-assign" backlog surfaced in MonthView's "Assign" badge and
 * (via DayHeader's `needsAssignment` prop) in DayView and WeekView too.
 *
 * Off markers (mode=null) are deliberately ignored — they're decided
 * non-working time, not pending work.
 */
export function dayHasUnassignedWorkingTime(rows: readonly AvailabilityRow[]): boolean {
  for (const row of rows) {
    if (row.mode === null) continue;
    const blockStart = timeToMinutes(row.startTime);
    const blockEnd = timeToMinutes(row.endTime);
    if (row.assignments.length === 0) return true;
    const sorted = [...row.assignments].sort(
      (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime),
    );
    let cursor = blockStart;
    for (const a of sorted) {
      const aStart = timeToMinutes(a.startTime);
      const aEnd = timeToMinutes(a.endTime);
      if (aStart > cursor) return true;
      if (aEnd > cursor) cursor = aEnd;
    }
    if (cursor < blockEnd) return true;
  }
  return false;
}

/** Inclusive range. Returns YYYY-MM-DD strings that fail `isDayDecided`. */
export function missingDaysInRange(
  rows: readonly AvailabilityRow[],
  from: string,
  to: string,
): string[] {
  const byDate = new Map<string, AvailabilityRow[]>();
  for (const r of rows) {
    const list = byDate.get(r.date) ?? [];
    list.push(r);
    byDate.set(r.date, list);
  }
  const missing: string[] = [];
  let cursor = from;
  while (cursor <= to) {
    const dayRows = byDate.get(cursor) ?? [];
    if (!isDayDecided(dayRows)) missing.push(cursor);
    cursor = nextDate(cursor);
  }
  return missing;
}

export interface AssignmentHit {
  block: AvailabilityRow;
  /** Null when the block has no assignment covering this minute. */
  assignment: BlockAssignmentRow | null;
}

/**
 * Find the working block (and, if present, the matching block assignment) at
 * a given minute-of-day, against pre-filtered blocks for that day.
 *
 * Three outcomes:
 *   • null                              — no bookable block here (no block
 *                                          at this minute, or only an
 *                                          off-mode block).
 *   • { block, assignment: null }       — inside a working block but the
 *                                          admin hasn't assigned this minute
 *                                          yet ("pending assignment").
 *   • { block, assignment }             — inside a working block AND an
 *                                          assignment covers this minute
 *                                          ("bookable").
 *
 * `minute` is minutes-since-midnight (0–1439). End times are exclusive on
 * both the block and assignment side, matching the convention used by
 * `computeBookableSlots` and the rest of the calendar timeline math.
 */
export function findAssignmentAt(
  blocks: readonly AvailabilityRow[],
  minute: number,
): AssignmentHit | null {
  for (const b of blocks) {
    if (b.mode === null) continue;
    const blockStart = timeToMinutes(b.startTime);
    const blockEnd = timeToMinutes(b.endTime);
    if (minute < blockStart || minute >= blockEnd) continue;

    for (const a of b.assignments) {
      const aStart = timeToMinutes(a.startTime);
      const aEnd = timeToMinutes(a.endTime);
      if (minute >= aStart && minute < aEnd) {
        return { block: b, assignment: a };
      }
    }
    return { block: b, assignment: null };
  }
  return null;
}

function nextDate(date: string): string {
  const [y, m, d] = date.split('-').map(Number);
  if (y === undefined || m === undefined || d === undefined) {
    throw new Error(`Invalid date: ${date}`);
  }
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + 1);
  return `${String(dt.getUTCFullYear())}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(
    dt.getUTCDate(),
  ).padStart(2, '0')}`;
}
